import {
  addFrameWithKitToCart,
  addFrameWithLensToCart,
} from '@theme/zrzs-quick-add-cart';
import { LensFlowController, formatMoney } from '@theme/zrzs-quick-add-lens-flow';
import {
  CartPanelController,
  findKitSibling,
} from '@theme/zrzs-quick-add-cart-panel';

/**
 * Só o nome limpo (sem "- grau/sol | tamanho") — usado no seletor/resumo
 * do grau retroativo, onde o Figma mostra "haru masp", não "haru - sol | m".
 * @param {any} item
 * @returns {string}
 */
function productDisplayNameOnly(item) {
  return (item.product_title || '').split('-')[0].trim();
}

/** @type {Map<string, Promise<Document | null>>} */
const productPageCache = new Map();

/**
 * @param {string} url
 * @returns {Promise<Document | null>}
 */
function fetchProductPage(url) {
  if (productPageCache.has(url)) return productPageCache.get(url);
  const promise = fetch(url)
    .then(response => (response.ok ? response.text() : null))
    .then(html =>
      html ? new DOMParser().parseFromString(html, 'text/html') : null
    )
    .catch(() => null);
  productPageCache.set(url, promise);
  return promise;
}

/**
 * Mesmo padrão de fetchLensPages em zrzs-quick-add-lens-flow.js.
 * @param {string} productUrl
 * @returns {Promise<string | null>}
 */
async function fetchMeasurementsFragment(productUrl) {
  const doc = await fetchProductPage(productUrl);
  const body = doc?.querySelector(
    '#frame-measurements-modal .main-product-accordion__modal-body'
  );
  return body ? body.innerHTML : null;
}

/** @type {any} */
let measurementsHostAccordion = null;
/** @type {string | null} */
let measurementsHostUrl = null;

/**
 * blocks/main-product-accordion.liquid (e o modal dentro dele) só renderiza
 * na PDP — fora dela cria um <main-product-accordion> escondido com o modal
 * fetchado, pra reaproveitar a mesma classe/CSS/controller de lá.
 * @param {string} productUrl
 * @returns {Promise<any>}
 */
async function ensureMeasurementsHost(productUrl) {
  if (measurementsHostAccordion && measurementsHostUrl === productUrl) {
    return measurementsHostAccordion;
  }
  const doc = await fetchProductPage(productUrl);
  const modal = doc?.querySelector('#frame-measurements-modal');
  if (!modal) return null;

  if (measurementsHostAccordion) measurementsHostAccordion.remove();
  const host = document.createElement('main-product-accordion');
  host.style.display = 'none';
  host.appendChild(modal.cloneNode(true));
  document.body.appendChild(host);

  measurementsHostAccordion = host;
  measurementsHostUrl = productUrl;
  return host;
}

class ZRZSQuickAddOverlay extends HTMLElement {
  /** @type {CartPanelController} */
  cartPanelController;

  connectedCallback() {
    this.backdrop = this.querySelector('[data-zqao-backdrop]');
    this.confirmPanel = this.querySelector('[data-zqao-panel="confirm"]');
    this.cartPanelEl = this.querySelector('[data-zqao-panel="cart"]');
    this.lensPanel = this.querySelector('[data-zqao-panel="lens-flow"]');

    this.confirmTitle = this.querySelector('[data-zqao-confirm-title]');
    this.confirmTitleDefaultText = this.confirmTitle
      ? this.confirmTitle.textContent.trim()
      : '';
    this.confirmImage = this.querySelector('[data-zqao-confirm-image]');
    this.confirmName = this.querySelector('[data-zqao-confirm-name]');
    this.confirmMeta = this.querySelector('[data-zqao-confirm-meta]');
    this.confirmErrorEl = this.querySelector('[data-zqao-confirm-error]');
    this.confirmContinueButton = this.querySelector(
      '[data-zqao-confirm-continue]'
    );
    this.sizeSection = this.querySelector('[data-zqao-size-section]');
    this.sizeOptionsList = this.querySelector('[data-zqao-size-options]');
    this.sizeGuideButton = this.querySelector('[data-zqao-size-guide]');
    this.currentMeasurementsUrl = null;
    if (this.sizeGuideButton) {
      this.sizeGuideButton.addEventListener(
        'click',
        this.handleMeasurementsGuideClick
      );
    }

    // Grau retroativo em armações solares já no carrinho (ver openSolarGrauSelector).
    this.solarGrauSheet = this.querySelector('[data-zqao-solar-grau-sheet]');
    this.solarGrauList = this.querySelector('[data-zqao-solar-grau-list]');
    this.solarGrauContinueButton = this.querySelector(
      '[data-zqao-solar-grau-continue]'
    );
    this.solarGrauCardTemplate = document.getElementById(
      'zqao-solar-grau-card-template'
    );
    /** @type {'confirm' | 'cart' | null} onde o lensFlow volta ao fechar/terminar
     *  — dinâmico, ver onExit abaixo; null fecha o overlay direto (ver openLensFlow). */
    this.lensFlowExitPanel = 'confirm';
    const solarGrauCloseButton = this.querySelector(
      '[data-zqao-solar-grau-close]'
    );
    if (solarGrauCloseButton) {
      solarGrauCloseButton.addEventListener('click', () =>
        this.closeSolarGrauSelector()
      );
    }
    if (this.solarGrauContinueButton) {
      this.solarGrauContinueButton.addEventListener('click', () =>
        this.handleSolarGrauSelectorContinue()
      );
    }

    // Exclui "continuar comprando" — ele comita antes de fechar (handleConfirmContinueClick).
    this.querySelectorAll(
      '[data-zqao-close], [data-zqao-continue]:not([data-zqao-confirm-continue])'
    ).forEach(el => el.addEventListener('click', this.close));
    if (this.backdrop) {
      this.backdrop.addEventListener('click', this.close);
    }
    if (this.confirmContinueButton) {
      this.confirmContinueButton.addEventListener(
        'click',
        this.handleConfirmContinueClick
      );
    }

    this.viewCartButton = this.querySelector('[data-zqao-view-cart]');
    if (this.viewCartButton) {
      this.viewCartButton.addEventListener(
        'click',
        this.handlePrimaryConfirmClick
      );
    }

    this.cartPanelController = new CartPanelController({
      root: this,
      sourceId: 'zrzs-quick-add-overlay',
      isVisible: () => Boolean(this.cartPanelEl) && !this.cartPanelEl.hidden,
      onGrauBannerClick: () => this.openSolarGrauSelector(),
      onContinueShopping: () => this.close(),
    });
    this.cartPanelController.connect();

    this.lensFlow = new LensFlowController(
      {
        content: this.querySelector('[data-zqao-lens-content]'),
        header: this.querySelector('.zrzs-quick-add-overlay__lens-header'),
        backButton: this.querySelector('[data-zqao-lens-back]'),
        title: this.querySelector('[data-zqao-lens-title]'),
        stepCounter: this.querySelector('[data-zqao-lens-step]'),
        subtitle: this.querySelector('[data-zqao-lens-subtitle]'),
        continueButton: this.querySelector('[data-zqao-lens-continue]'),
        skipButton: this.querySelector('[data-zqao-lens-skip]'),
        footer: this.querySelector('.zrzs-quick-add-overlay__lens-footer'),
        popup: {
          root: this.querySelector('[data-zqao-popup]'),
          title: this.querySelector('[data-zqao-popup-title]'),
          body: this.querySelector('[data-zqao-popup-body]'),
          closeButton: this.querySelector('[data-zqao-popup-close]'),
          backButton: this.querySelector('[data-zqao-popup-back]'),
          continueButton: this.querySelector('[data-zqao-popup-continue]'),
        },
      },
      {
        onOpen: () => this.showPanel('lens-flow'),
        onReopenAnimated: () => this.reopenAnimated(),
        // Grau retroativo em desktop volta pro drawer real, não pro painel interno.
        onExit: message => {
          // message só vem de falha real de fetch (ver LensFlowController#open)
          // — mesmo caso de exitPanel null (confirmação nunca exibida), volta
          // pra ela em vez de fechar calado, já que populateConfirmPanel
          // sempre roda antes (ver handlePendingOpen) e ela já tem o produto certo.
          if (message) {
            this.setConfirmError(message);
            this.showPanel('confirm');
            return;
          }
          // null: grau puro sem escolha de tamanho pulou a tela de
          // confirmação (ver handlePendingOpen) — ela nunca foi exibida,
          // então "voltar" na 1ª etapa do fluxo de lentes tem que fechar o
          // overlay, não mostrar um painel que o usuário nunca viu.
          if (this.lensFlowExitPanel === null) {
            this.close();
            return;
          }
          // openCartPanel decide entre painel (mobile), drawer (desktop) e fechar (/cart).
          if (this.lensFlowExitPanel === 'cart') {
            this.openCartPanel();
            return;
          }
          this.showPanel(this.lensFlowExitPanel);
        },
        onAddFrameAndShowCart: () => this.commitPendingAddAndShowCart(),
        onAddConfiguredLensToCart: config =>
          this.commitLensAddAndShowCart(config),
        // Admin (settings_schema.json, grupo "Adição facilitada") — ver
        // data-stores-map-image em zrzs-quick-add-overlay.liquid.
        storesMapImageUrl: this.dataset.storesMapImage || null,
        consultantWhatsappNumber: this.dataset.consultantWhatsappNumber || null,
      }
    );

    document.addEventListener('zrzs:quick-add:open', this.handleOpen);
    document.addEventListener(
      'zrzs:quick-add:open-cart',
      this.handleOpenCartIcon
    );
    document.addEventListener(
      'zrzs:quick-add:open-solar-grau',
      this.handleOpenSolarGrau
    );
    document.addEventListener('keydown', this.handleKeydown);
    document.addEventListener('click', this.handleSolarGrauOutsideClick);
  }

  disconnectedCallback() {
    document.removeEventListener('zrzs:quick-add:open', this.handleOpen);
    document.removeEventListener(
      'zrzs:quick-add:open-cart',
      this.handleOpenCartIcon
    );
    document.removeEventListener(
      'zrzs:quick-add:open-solar-grau',
      this.handleOpenSolarGrau
    );
    document.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('click', this.handleSolarGrauOutsideClick);
    if (this.cartPanelController) this.cartPanelController.disconnect();
  }

  /**
   * Fecha o seletor de grau retroativo ao clicar fora dele.
   * @param {MouseEvent} event
   */
  handleSolarGrauOutsideClick = event => {
    const sheet = this.solarGrauSheet;
    if (!sheet || sheet.hidden) return;

    const target = /** @type {Node} */ (event.target);
    if (sheet.contains(target)) return;

    // O clique que abre o sheet borbulha até aqui e se via como "fora" —
    // existe uma cópia do link por host (overlay mobile e drawer desktop).
    const bannerLinks = document.querySelectorAll(
      '[data-zqao-grau-banner-link]'
    );
    for (const link of bannerLinks) {
      if (link.contains(target)) return;
    }

    this.closeSolarGrauSelector();
  };

  /**
   * @param {CustomEvent<Record<string, any>>} event
   */
  handleOpen = event => {
    const detail = event.detail || {};
    if (detail.pending) {
      this.handlePendingOpen(detail);
    } else {
      this.handleAddedOpen(detail);
    }
  };

  handleOpenCartIcon = () => {
    this.open();
    this.openCartPanel();
  };

  /** Sem this.open() — o drawer fica aberto atrás, só o seletor aparece por cima. Em desktop reaproveita o estado do drawer aberto (o mais recente, é o que o cliente está vendo); sem ele, busca a section. */
  handleOpenSolarGrau = async () => {
    const isDesktop = window.matchMedia('(min-width: 750px)').matches;
    const drawer = /** @type {any} */ (
      isDesktop && document.querySelector('zrzs-quick-add-cart-drawer')
    );
    const drawerController =
      drawer && drawer.isOpen && drawer.cartPanelController;
    if (drawerController && drawerController.lastCart) {
      this.cartPanelController.solarGrauCandidates =
        drawerController.solarGrauCandidates;
      this.cartPanelController.lastCartItems = drawerController.lastCartItems;
    } else {
      await this.cartPanelController.refreshCartPanel();
    }
    this.openSolarGrauSelector();
  };

  /**
   * Disparado quando a PDP (assets/lens-selection.js) já rodou /cart/add.js — a variante (com tamanho) já foi resolvida lá.
   * @param {{ cart?: { items: any[] }, hasLensBasket?: boolean }} detail
   */
  handleAddedOpen(detail) {
    const responseItems = detail.cart && detail.cart.items;
    if (!Array.isArray(responseItems) || responseItems.length === 0) return;

    const frameItem = responseItems[responseItems.length - 1];
    this.isPendingLensSelection = Boolean(detail.hasLensBasket);
    this.isPendingSolar = false;
    this.isPendingFromPdp = false;
    this.forcesLensFlow = this.isPendingLensSelection;
    this.isPendingCartAdd = false;
    this.pendingFrame = null;
    // detail não traz hasMeasurements aqui (add já rodou fora do fluxo pending) — mesma checagem de zrzs-quick-add-pdp.js.
    this.pendingFrameHasMeasurements = Boolean(
      document.querySelector('.product-frame-measurements')
    );
    this.sizes = null;
    this.selectedSizeVariantId = null;
    this.pendingProductUrl = frameItem.url || null;
    this.lensFlow.reset();
    // /cart/add.js não traz alternative_title — split é fallback best-effort.
    this.populateConfirmPanel({
      ...frameItem,
      product_title: (frameItem.product_title || '').split('-')[0].trim(),
    });
    this.renderSizeSection();
    this.showPanel('confirm');
    this.open();
  }

  /**
   * Clique no card/PLP — ainda não adicionado; active_variant não resolve tamanho, então /cart/add.js real só roda em commitPendingAdd.
   * @param {{
   *   item: any,
   *   frameVariantId: string,
   *   frameVariantTitle: string,
   *   kitVariantId: string | null,
   *   hasLensBasket: boolean,
   *   isSolar: boolean,
   *   fromPdp?: boolean,
   *   sizes: { optionName: string, values: Array<{ value: string, variantId: number, variantTitle?: string, price?: number, image?: string, inStock: boolean, hasMeasurements?: boolean, url?: string }> } | null,
   *   hasMeasurements: boolean,
   *   kitCaseBanner: string | null,
   *   kitCaseDescription: string | null,
   *   giftsData: any[] | null,
   *   onSettled?: () => void,
   * }} detail
   */
  async handlePendingOpen(detail) {
    // Protege contra 2 cliques em produtos diferentes se sobrepondo — a
    // chamada mais nova sempre vence; uma mais velha que resolva depois
    // (commitPendingAdd/openLensFlow) desiste antes de tocar a UI de novo.
    const token = (this.pendingOpenToken = (this.pendingOpenToken || 0) + 1);
    // Reabilita o botão que disparou o clique (card ou PDP — ver
    // zrzs-quick-add-trigger.js/zrzs-quick-add-pdp.js) só depois que este
    // clique específico terminar de ser processado, `finally` cobre TODOS
    // os ramos abaixo (inclusive erro) — sem isso, duplo/triplo clique
    // duplicava o item pra qualquer solar sem escolha de tamanho (o commit
    // agora roda direto aqui, sem passar mais pelo botão "ver carrinho" do
    // overlay, que era onde esse guard vivia antes).
    try {
      // Resumir: mesma armação já em configuração, fechada no meio sem
      // concluir (ex.: clique fora, agora sempre fecha) — reabre de onde
      // parou, sem refazer fetch nem resetar o fluxo de lentes.
      // isPendingCartAdd vira false quando a adição conclui com sucesso
      // (ver commitPendingAdd/commitLensAddAndShowCart, que também limpam
      // pendingFrame) — garante que uma compra já finalizada nunca "resume"
      // sozinha numa tentativa nova pro mesmo produto.
      const canResume =
        this.isPendingCartAdd &&
        this.pendingFrame &&
        String(this.pendingFrame.variantId) === String(detail.frameVariantId) &&
        this.lensFlow.history.length > 0;
      if (canResume) {
        this.open();
        this.lensFlow.resumeCurrentStep();
        return;
      }

      this.isPendingLensSelection = Boolean(detail.hasLensBasket);
      this.isPendingSolar = Boolean(detail.isSolar);
      this.isPendingFromPdp = Boolean(detail.fromPdp);
      // No card, solar com grau pode ser levado sem lente (configura
      // depois pelo carrinho — ver banner "precisa de grau?"); grau puro
      // não, força "escolher lentes e finalizar". Na PDP, cesta de lentes
      // SEMPRE força o fluxo de lentes, mesmo solar com grau — só produto
      // sem cesta nenhuma adiciona direto por ali (ver
      // zrzs-quick-add-pdp.js#handleDesktopNoBasketClick). Ver
      // populateConfirmPanel/handleConfirmContinueClick.
      this.forcesLensFlow =
        this.isPendingLensSelection &&
        (!this.isPendingSolar || this.isPendingFromPdp);
      this.isPendingCartAdd = true;
      this.pendingFrame = {
        variantId: detail.frameVariantId,
        variantTitle: detail.frameVariantTitle,
        kitVariantId: detail.kitVariantId,
        kitCaseBanner: detail.kitCaseBanner || null,
        kitCaseDescription: detail.kitCaseDescription || null,
        giftsData: detail.giftsData || null,
      };
      this.pendingFrameHasMeasurements = Boolean(detail.hasMeasurements);
      this.sizes = detail.sizes || null;
      this.selectedSizeVariantId = null;
      this.pendingProductUrl = (detail.item && detail.item.url) || null;
      this.lensFlow.reset();
      this.populateConfirmPanel(detail.item || {});
      this.renderSizeSection();

      if (this.needsSizeChoice) {
        // 2+ tamanhos em estoque — espera o usuário escolher, painel normal.
        this.showPanel('confirm');
        this.open();
        return;
      }

      if (this.forcesLensFlow) {
        // Grau puro (em qualquer tela) ou cesta de lentes na PDP (mesmo
        // solar com grau), sem escolha de tamanho real: pula a tela de
        // confirmação, vai direto pro fluxo de lentes (mesmo fluxo de
        // sempre, só sem o clique extra em "escolher lentes e finalizar").
        this.open();
        await this.openLensFlow(null);
        return;
      }

      // Sem cesta de lentes, ou solar (puro ou com grau) vindo do card: sem
      // escolha de tamanho real, adiciona assim que o usuário clicou, sem
      // esperar outro clique — "continuar comprando" vira um mero fechar
      // (ver handleConfirmContinueClick).
      const added = await this.commitPendingAdd();
      if (token !== this.pendingOpenToken) return;
      if (!added) {
        // Falha no /cart/add.js só ia pro console — carrinho abria mesmo
        // assim, parecendo sucesso. Mostra o painel de confirmação (já
        // populado acima) com erro visível em vez disso.
        this.setConfirmError(
          'não foi possível adicionar ao carrinho agora. tenta de novo.'
        );
        this.showPanel('confirm');
        this.open();
        return;
      }
      this.updateViewCartButtonText();
      // Abre o carrinho direto em vez do overlay "adicionado ao carrinho" —
      // pra voltar a mostrar o overlay de confirmação aqui, troque a linha
      // abaixo por `this.showAddedConfirmation();` (método abaixo, intacto).
      this.open();
      await this.openCartPanel();
    } finally {
      detail.onSettled?.();
    }
  }

  /**
   * Estado "já adicionado" do painel de confirmação (overlay) — some
   * quando o clique de adicionar não precisa esperar tamanho. Não é
   * chamado no momento (ver handlePendingOpen, que abre o carrinho direto
   * em vez disso), mas fica pronto: pra usar de novo, é só chamar este
   * método no lugar de `this.open(); await this.openCartPanel();`.
   */
  showAddedConfirmation() {
    this.updateViewCartButtonText();
    this.showPanel('confirm');
    this.open();
  }

  /**
   * item.product_title já vem limpo (sem "Tamanho X") do chamador.
   * @param {any} item
   */
  populateConfirmPanel(item) {
    this.setConfirmError('');
    if (this.confirmImage) {
      this.confirmImage.src = item.image || '';
      this.confirmImage.alt = item.product_title || '';
    }
    if (this.confirmName) {
      this.confirmName.textContent = item.product_title || '';
    }
    this.pendingFramePriceCents =
      item.final_price !== undefined ? item.final_price : item.price || 0;
    const price = formatMoney(this.pendingFramePriceCents);
    if (this.confirmMeta) {
      const variantLabel =
        item.variant_title && item.variant_title !== 'Default Title'
          ? item.variant_title
          : '';
      this.confirmMeta.textContent = '';
      if (variantLabel) {
        this.confirmMeta.append(`${variantLabel} - `);
      }
      this.confirmMeta.appendChild(this.buildPriceSpan(price));
    }
    this.updateViewCartButtonText();
    if (this.confirmContinueButton) {
      // No card, solar com grau pode ser levado sem lente (configura
      // depois pelo carrinho); grau puro sem lente não é um produto
      // vendável sozinho. Na PDP, cesta de lentes nunca libera "continuar
      // comprando" sem configurar (ver forcesLensFlow).
      this.confirmContinueButton.disabled = this.forcesLensFlow;
    }
  }

  /**
   * "escolher lentes e finalizar" pra grau puro (qualquer tela) e pra
   * cesta de lentes na PDP (mesmo solar com grau) — ver forcesLensFlow.
   * Solar do card continua adicionando a armação direto (grau vem depois
   * pelo banner "precisa de grau?").
   */
  updateViewCartButtonText() {
    if (!this.viewCartButton) return;
    if (this.forcesLensFlow) {
      this.viewCartButton.textContent = 'escolher lentes e finalizar';
      return;
    }
    this.viewCartButton.textContent = this.isPendingCartAdd
      ? 'adicionar ao carrinho'
      : 'ver carrinho';
  }

  /**
   * Seletor de tamanho e subtotal aparecem juntos. Só conta como "escolha
   * real" quando há 2+ tamanhos EM ESTOQUE — com 0 ou 1 disponível (mesmo
   * que outros tamanhos existam fora de estoque, ou que o tamanho "atual"
   * da página não seja o disponível), seleciona sozinho e pula a etapa.
   */
  renderSizeSection() {
    const allSizes =
      this.sizes && Array.isArray(this.sizes.values) ? this.sizes.values : [];
    const availableSizes = allSizes.filter(sizeValue => sizeValue.inStock);
    this.needsSizeChoice = availableSizes.length > 1;

    if (this.sizeSection) this.sizeSection.hidden = !this.needsSizeChoice;

    if (this.sizeOptionsList) {
      this.sizeOptionsList.innerHTML = '';
      if (this.needsSizeChoice) {
        allSizes.forEach(sizeValue => this.buildSizePill(sizeValue));
      }
    }

    if (!this.needsSizeChoice && allSizes.length) {
      const onlyOption =
        availableSizes[0] ||
        allSizes.find(sizeValue => sizeValue.current) ||
        null;
      if (onlyOption) this.selectedSizeVariantId = onlyOption.variantId;
    }

    // "continuar comprando" some pro resto desta sessão do painel assim que
    // existir escolha real de tamanho — não volta mesmo depois de escolhido.
    if (this.confirmContinueButton) {
      this.confirmContinueButton.hidden = this.needsSizeChoice;
    }

    const currentSize = this.needsSizeChoice
      ? allSizes.find(sizeValue => sizeValue.current)
      : allSizes.find(
          sizeValue => sizeValue.variantId === this.selectedSizeVariantId
        ) || null;
    this.updateMeasurementsGuideVisibility(currentSize);
    this.updateConfirmTitle();
    this.updatePrimaryButtonState();
  }

  /**
   * "escolha seu tamanho" durante toda a etapa em que há escolha real de
   * tamanho — não depende de já ter uma seleção, porque `buildSizePill`
   * pré-marca o tamanho "atual" como selecionado antes de qualquer clique
   * do usuário (pra habilitar o botão principal de cara); usar
   * `!selectedSizeVariantId` aqui nunca ficaria true de verdade.
   */
  updateConfirmTitle() {
    this.applyConfirmTitle(this.needsSizeChoice ? 'escolha seu tamanho' : null);
  }

  /** @param {string | null} text texto temporário; null/vazio volta ao padrão do HTML. */
  applyConfirmTitle(text) {
    if (!this.confirmTitle) return;
    this.confirmTitle.textContent = text || this.confirmTitleDefaultText;
  }

  /**
   * Guia de medidas é por produto/tamanho (metacampo technical_drawing),
   * independente do seletor de tamanho ter ou não múltiplas opções.
   * @param {{ hasMeasurements?: boolean, url?: string } | null | undefined} sizeValue
   */
  updateMeasurementsGuideVisibility(sizeValue) {
    if (!this.sizeGuideButton) return;

    const hasMeasurements = sizeValue
      ? Boolean(sizeValue.hasMeasurements)
      : Boolean(this.pendingFrameHasMeasurements);
    this.currentMeasurementsUrl =
      (sizeValue && sizeValue.url) || this.pendingProductUrl || null;
    this.sizeGuideButton.hidden = !hasMeasurements;
  }

  /** Na PDP reaproveita o <main-product-accordion> real; fora dela, o host fantasma (ensureMeasurementsHost). */
  handleMeasurementsGuideClick = async () => {
    if (!this.currentMeasurementsUrl) return;

    let accordion = /** @type {any} */ (
      document.querySelector('main-product-accordion')
    );
    if (accordion) {
      const fragment = await fetchMeasurementsFragment(
        this.currentMeasurementsUrl
      );
      const body = accordion.querySelector(
        '#frame-measurements-modal .main-product-accordion__modal-body'
      );
      if (fragment && body) body.innerHTML = fragment;
    } else {
      accordion = await ensureMeasurementsHost(this.currentMeasurementsUrl);
    }

    if (
      accordion &&
      typeof accordion.openFrameMeasurementsModal === 'function'
    ) {
      accordion.openFrameMeasurementsModal(true);
    }
  };

  /**
   * Cada tamanho é um produto irmão, não uma variant option — `current` nunca é desabilitado por estoque.
   * @param {{ value: string, variantId: number, variantTitle?: string, price?: number, image?: string, inStock: boolean, current?: boolean, hasMeasurements?: boolean, url?: string }} sizeValue
   */
  buildSizePill(sizeValue) {
    if (!this.sizeOptionsList) return;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'zrzs-quick-add-overlay__size-pill';
    button.textContent = sizeValue.value;
    button.disabled = !sizeValue.inStock && !sizeValue.current;
    button.addEventListener('click', () => {
      this.sizeOptionsList
        .querySelectorAll('.zrzs-quick-add-overlay__size-pill')
        .forEach(pill => pill.classList.remove('is-selected'));
      button.classList.add('is-selected');
      this.selectedSizeVariantId = sizeValue.variantId;
      this.updateMeasurementsGuideVisibility(sizeValue);
      this.updatePrimaryButtonState();
      this.updateConfirmPreviewForSize(sizeValue);
    });
    this.sizeOptionsList.appendChild(button);
  }

  /**
   * Cada tamanho é um produto-irmão independente (imagem/preço podem
   * diferir do item originalmente exibido no card/PDP) — sem isso, o
   * preview ficava preso no tamanho mostrado inicialmente mesmo depois de
   * escolher outro, dando a impressão de que "veio outro produto" só na
   * hora de ver o carrinho.
   * @param {{ variantTitle?: string, price?: number, image?: string }} sizeValue
   */
  updateConfirmPreviewForSize(sizeValue) {
    if (sizeValue.image && this.confirmImage) {
      this.confirmImage.src = sizeValue.image;
    }
    if (sizeValue.price !== undefined) {
      this.pendingFramePriceCents = sizeValue.price;
    }
    if (this.confirmMeta) {
      const variantLabel =
        sizeValue.variantTitle && sizeValue.variantTitle !== 'Default Title'
          ? sizeValue.variantTitle
          : '';
      this.confirmMeta.textContent = '';
      if (variantLabel) {
        this.confirmMeta.append(`${variantLabel} - `);
      }
      this.confirmMeta.appendChild(
        this.buildPriceSpan(formatMoney(this.pendingFramePriceCents))
      );
    }
  }

  updatePrimaryButtonState() {
    if (!this.viewCartButton) return;
    this.viewCartButton.disabled =
      this.needsSizeChoice && !this.selectedSizeVariantId;
  }

  /** @param {MouseEvent} event */
  handlePrimaryConfirmClick = async event => {
    event.preventDefault();
    if (this.viewCartButton && this.viewCartButton.disabled) return;

    // No card, solar (puro ou com grau) nunca entra no fluxo de lentes por
    // aqui — sempre adiciona a armação direto (grau vem depois, pelo
    // carrinho). Na PDP, cesta de lentes sempre entra (ver forcesLensFlow).
    if (this.forcesLensFlow) {
      this.openLensFlow();
      return;
    }

    if (this.isPendingCartAdd) {
      const added = await this.commitPendingAdd();
      if (!added) return;
    }
    this.openCartPanel();
  };

  /**
   * "continuar comprando" também comita antes de fechar; guard aqui é só defensivo.
   * @param {MouseEvent} event
   */
  handleConfirmContinueClick = async event => {
    event.preventDefault();
    if (this.confirmContinueButton && this.confirmContinueButton.disabled) {
      return;
    }
    // Libera pra solar com grau vindo do card (fica sem lente, configurável
    // depois pelo carrinho) — grau puro, e qualquer cesta de lentes na
    // PDP, nunca chegam aqui com o botão habilitado (ver forcesLensFlow).
    if (!this.forcesLensFlow && this.isPendingCartAdd) {
      const added = await this.commitPendingAdd();
      if (!added) return;
    }
    this.close();
  };

  async commitPendingAddAndShowCart() {
    if (this.isPendingCartAdd) {
      const added = await this.commitPendingAdd();
      if (!added) return;
    }
    this.openCartPanel();
  }

  /**
   * Título da variante que vai pro /cart/add.js: se o tamanho escolhido é
   * de um produto-irmão (catálogo modela tamanho como produto separado,
   * não variant option — ver zrzs-quick-add-trigger.liquid), o título
   * certo é o DELE, não o do item originalmente exibido no card/PDP.
   * Sem isso, _productVariantTitleValue nunca batia com `${variant_title}
   * - ${variant_id}` do próprio item devolvido pelo /cart.js, e
   * isAccompanyingItem (zrzs-quick-add-cart-panel.js) classificava a
   * armação como "acompanhante" de si mesma — some da lista, mas soma no total.
   * @returns {string}
   */
  getPendingFrameVariantTitle() {
    if (this.selectedSizeVariantId && this.sizes?.values) {
      const match = this.sizes.values.find(
        sizeValue => sizeValue.variantId === this.selectedSizeVariantId
      );
      if (match?.variantTitle) return match.variantTitle;
    }
    return this.pendingFrame?.variantTitle || '';
  }

  /**
   * Roda o /cart/add.js real — adiado até aqui pra nunca adicionar um tamanho antes do usuário escolher.
   * @returns {Promise<boolean>}
   */
  async commitPendingAdd() {
    if (!this.pendingFrame) return true;

    const frameVariantId =
      this.selectedSizeVariantId || this.pendingFrame.variantId;

    this.setConfirmError('');
    if (this.viewCartButton) this.viewCartButton.disabled = true;
    try {
      const result = await addFrameWithKitToCart({
        frameVariantId,
        frameVariantTitle: this.getPendingFrameVariantTitle(),
        kitVariantId: this.pendingFrame.kitVariantId,
        hasLensBasket: this.isPendingLensSelection,
        kitCaseBanner: this.pendingFrame.kitCaseBanner,
        kitCaseDescription: this.pendingFrame.kitCaseDescription,
        framePriceCents: this.pendingFramePriceCents,
        giftsData: this.pendingFrame.giftsData,
      });
      const addedItems = (result.cart && result.cart.items) || [];
      const addedFrame = addedItems[addedItems.length - 1];
      if (addedFrame && addedFrame.url) this.pendingProductUrl = addedFrame.url;
      this.isPendingCartAdd = false;
      // Concluído com sucesso — limpa pra uma tentativa nova pro MESMO
      // produto nunca ler isso como "sessão em andamento" (ver
      // handlePendingOpen, condição de "resumir").
      this.pendingFrame = null;
      return true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[QuickAddOverlay] Erro ao adicionar ao carrinho:', error);
      return false;
    } finally {
      if (this.viewCartButton) this.viewCartButton.disabled = false;
    }
  }

  /** @param {string} message string vazia esconde. */
  setConfirmError(message) {
    if (!this.confirmErrorEl) return;
    this.confirmErrorEl.textContent = message;
    this.confirmErrorEl.hidden = !message;
  }

  /**
   * "adicionar ao carrinho" do resumo — roda /cart/add.js do fluxo de grau numa chamada só.
   * @param {{ lens: { variantRef: string | null, productSlug: string | null, variantTitle: string | null }, prescription: object | null }} config
   * @returns {Promise<boolean>} false faz o resumo mostrar um erro visível (antes ficava mudo).
   */
  async commitLensAddAndShowCart(config) {
    if (!this.pendingFrame) {
      // eslint-disable-next-line no-console
      console.error(
        '[QuickAddOverlay] Sem armação pendente pra vincular à lente — item de lente não adicionado.'
      );
      this.openCartPanel();
      return true;
    }

    const frameVariantId =
      this.selectedSizeVariantId || this.pendingFrame.variantId;

    try {
      await addFrameWithLensToCart({
        frameVariantId,
        frameVariantTitle: this.getPendingFrameVariantTitle(),
        kitVariantId: this.pendingFrame.kitVariantId,
        lens: config.lens,
        prescription: config.prescription,
        kitCaseBanner: this.pendingFrame.kitCaseBanner,
        kitCaseDescription: this.pendingFrame.kitCaseDescription,
        framePriceCents: this.pendingFramePriceCents,
        giftsData: this.pendingFrame.giftsData,
      });
      this.isPendingCartAdd = false;
      // Concluído com sucesso — limpa pra uma tentativa nova pro MESMO
      // produto nunca ler isso como "sessão em andamento" (ver
      // handlePendingOpen, condição de "resumir").
      this.pendingFrame = null;
      this.openCartPanel();
      return true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        '[QuickAddOverlay] Erro ao adicionar lente ao carrinho:',
        error
      );
      return false;
    }
  }

  /**
   * @param {'confirm' | null} [exitPanel] pra onde "voltar" na 1ª etapa do
   *   fluxo de lentes deve ir — 'confirm' (default) se o painel de
   *   confirmação foi mostrado antes; null se foi pulado (grau puro sem
   *   escolha de tamanho), caso em que "voltar" deve fechar o overlay.
   */
  async openLensFlow(exitPanel = 'confirm') {
    // Se um fluxo em lote (grau retroativo, ver openSolarGrauSelector) foi
    // aberto e abandonado (X no meio), isso ainda apontaria pro carrinho.
    this.lensFlowExitPanel = exitPanel;
    this.viewCartButton.disabled = true;
    await this.lensFlow.open(this.pendingProductUrl, {
      image: (this.confirmImage && this.confirmImage.src) || '',
      title: (this.confirmName && this.confirmName.textContent) || '',
      price: formatMoney(this.pendingFramePriceCents || 0),
      priceCents: this.pendingFramePriceCents || 0,
    });
    this.viewCartButton.disabled = false;
  }

  /**
   * O texto ao redor usa text-transform: lowercase (deixaria "R$" como "r$") — isola o preço num span uppercase.
   * @param {string} price
   */
  buildPriceSpan(price) {
    const span = document.createElement('span');
    span.className = 'zrzs-quick-add-overlay__price';
    span.textContent = price;
    return span;
  }

  showPanel(name) {
    if (this.confirmPanel) this.confirmPanel.hidden = name !== 'confirm';
    if (this.cartPanelEl) this.cartPanelEl.hidden = name !== 'cart';
    if (this.lensPanel) this.lensPanel.hidden = name !== 'lens-flow';
  }

  open = () => {
    this.setAttribute('aria-hidden', 'false');
    this.classList.add('zrzs-quick-add-overlay--open');
    this.lockBodyScroll();
  };

  close = () => {
    this.setAttribute('aria-hidden', 'true');
    this.classList.remove('zrzs-quick-add-overlay--open');
    this.unlockBodyScroll();
    // Sem isso, um fetch de página de lentes ainda em voo (ver
    // LensFlowController#open) resolvia depois do fechamento e empurrava a
    // página 1 por cima do painel que o usuário já tinha saído.
    this.lensFlow?.invalidatePendingOpen();
    // Multifocal não tem seta de volta pra página 1 — fechar nessa etapa
    // (ou qualquer uma depois dela) deixaria o resume reabrir preso lá.
    // Reseta aqui pra reabrir sempre do zero nesse caso.
    if (this.lensFlow?.isInMultifocalFlow()) {
      this.lensFlow.reset();
    }
  };

  /**
   * Replay da transição de abertura (sheet "fecha" e "abre" de novo) sem
   * desmontar nada — overlay continua tecnicamente aberto (scroll do body
   * travado, aria-hidden inalterado), só a classe que dirige a animação
   * de slide/fade pisca. Só pra entradas específicas que devem parecer uma
   * tela nova "chegando" (ex.: popup de faixa não atendida indo pra
   * geolocalização) — nunca em navegação normal entre etapas do fluxo de
   * lentes, onde ficaria piscando a cada passo.
   */
  reopenAnimated = () => {
    const sheet = this.querySelector('.zrzs-quick-add-overlay__sheet');
    this.classList.remove('zrzs-quick-add-overlay--open');

    // Reabre só depois que a transição de FECHAR terminar de verdade —
    // com rAF duplo (sem esperar nada) a reabertura começava quase no
    // mesmo instante, de um ponto já quase aberto, e a animação de abrir
    // ficava imperceptível (bug relatado: só via fechar, nunca abrir).
    let reopened = false;
    const reopen = () => {
      if (reopened) return;
      reopened = true;
      this.classList.add('zrzs-quick-add-overlay--open');
    };
    if (sheet) sheet.addEventListener('transitionend', reopen, { once: true });
    // Rede de segurança — sheet não encontrado, ou transitionend não
    // dispara por algum motivo (ex.: prefers-reduced-motion zera a
    // duração): reabre de qualquer forma depois de um tempo generoso.
    setTimeout(reopen, 400);
  };

  /**
   * position:fixed em vez de overflow:hidden — evita rubber-banding no
   * iOS Safari. Idempotente: com o body já fixo, window.scrollY lê 0 (o
   * "scroll" é só o offset negativo, o documento não rola de verdade) —
   * chamar de novo (duplo clique no ícone/PDP, reabrir rápido) sobrescrevia
   * scrollPosition pra 0 e fazia a página saltar pro topo ao fechar.
   */
  lockBodyScroll() {
    if (document.body.classList.contains('modal-open')) return;
    this.scrollPosition = window.scrollY;
    document.body.style.top = `-${this.scrollPosition}px`;
    document.body.classList.add('modal-open');
  }

  /** Só desfaz se esta instância travou (ver lockBodyScroll). */
  unlockBodyScroll() {
    if (this.scrollPosition == null) return;

    document.body.classList.remove('modal-open');
    document.body.style.top = '';

    // Tema usa scroll-behavior: smooth (assets/base.css) — força 'auto' pra restaurar sem animar.
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlBehavior = html.style.scrollBehavior;
    const prevBodyBehavior = body.style.scrollBehavior;
    html.style.setProperty('scroll-behavior', 'auto', 'important');
    body.style.setProperty('scroll-behavior', 'auto', 'important');
    window.scrollTo(0, this.scrollPosition);
    this.scrollPosition = null;
    requestAnimationFrame(() => {
      if (prevHtmlBehavior) html.style.scrollBehavior = prevHtmlBehavior;
      else html.style.removeProperty('scroll-behavior');
      if (prevBodyBehavior) body.style.scrollBehavior = prevBodyBehavior;
      else body.style.removeProperty('scroll-behavior');
    });
  }

  handleKeydown = event => {
    if (
      event.key === 'Escape' &&
      this.getAttribute('aria-hidden') === 'false'
    ) {
      this.close();
    }
  };

  /**
   * Em desktop entrega pro <zrzs-quick-add-cart-drawer> real (com recomendações) em vez do painel de carrinho interno.
   * Na página do carrinho só fecha: a própria página é o carrinho e se atualiza
   * sozinha com o cart:update (component-cart-items.js) — sem drawer nem painel.
   */
  openCartPanel = async () => {
    if ((document.body.dataset.template || '').split('.')[0] === 'cart') {
      this.close();
      return;
    }
    if (window.matchMedia('(min-width: 750px)').matches) {
      this.close();
      document.dispatchEvent(
        new CustomEvent('zrzs:quick-add:open-cart-desktop')
      );
      return;
    }
    this.showPanel('cart');
    await this.cartPanelController.refreshCartPanel();
  };

  /**
   * Armação solar SEM lente ainda, elegível pra grau retroativo — candidatos
   * já calculados a cada render do carrinho (elegibilidade no Liquid,
   * snippets/zrzs-quick-add-cart-content.liquid; lidos por
   * CartPanelController#readSolarGrauCandidates).
   */
  openSolarGrauSelector() {
    const sheet = this.solarGrauSheet;
    const list = this.solarGrauList;
    if (!sheet || !list) return;
    list.innerHTML = '';
    (this.cartPanelController.solarGrauCandidates || []).forEach(candidate => {
      const node = this.buildSolarGrauSelectorCard(candidate);
      if (node) list.appendChild(node);
    });
    sheet.hidden = false;
  }

  closeSolarGrauSelector() {
    if (this.solarGrauSheet) this.solarGrauSheet.hidden = true;
  }

  /**
   * @param {{item: any, unitIndex: number}} candidate
   */
  buildSolarGrauSelectorCard(candidate) {
    if (!this.solarGrauCardTemplate) return null;
    const fragment = /** @type {DocumentFragment} */ (
      this.solarGrauCardTemplate.content.cloneNode(true)
    );
    const li = fragment.querySelector(
      '.zrzs-quick-add-overlay__solar-grau-card'
    );
    if (!li) return null;
    const { item } = candidate;

    const image = fragment.querySelector(
      '.zrzs-quick-add-overlay__cart-item-image'
    );
    if (image) {
      image.src = item.image || '';
      image.alt = item.product_title || '';
    }
    const name = fragment.querySelector(
      '.zrzs-quick-add-overlay__solar-grau-card-name'
    );
    if (name) name.textContent = productDisplayNameOnly(item);
    const meta = fragment.querySelector(
      '.zrzs-quick-add-overlay__solar-grau-card-meta'
    );
    if (meta) meta.textContent = item.variant_title || '';
    const price = fragment.querySelector(
      '.zrzs-quick-add-overlay__solar-grau-card-price'
    );
    if (price) price.textContent = formatMoney(item.price);

    return fragment;
  }

  /** "continuar" do seletor — abre o fluxo de lente em lote pras unidades marcadas. */
  handleSolarGrauSelectorContinue() {
    if (!this.solarGrauList) return;
    const checkboxes = this.solarGrauList.querySelectorAll(
      '.zrzs-quick-add-overlay__solar-grau-card-checkbox'
    );
    /** @type {Array<any>} */
    const frames = [];
    const candidates = this.cartPanelController.solarGrauCandidates || [];
    checkboxes.forEach((checkbox, index) => {
      if (!(checkbox instanceof HTMLInputElement) || !checkbox.checked) return;
      const candidate = candidates[index];
      if (!candidate) return;
      const { item } = candidate;
      // Kit já entregue na compra original — "move" pro grupo novo (nunca
      // duplica: a linha antiga do kit é decrementada junto da armação
      // antiga quando a fila terminar, ver handleSolarGrauBatchComplete).
      const kitSibling = findKitSibling(
        item,
        this.cartPanelController.lastCartItems || []
      );
      frames.push({
        frameVariantId: item.variant_id,
        frameVariantTitle: item.variant_title || '',
        cartItemKey: item.key,
        frameUrl: item.url,
        kitVariantId: kitSibling?.variant_id || null,
        kitCaseBanner: kitSibling?.properties?._caseBanner || null,
        kitCaseDescription: kitSibling?.properties?._caseDescription || null,
        productSummary: {
          image: item.image || '',
          title: productDisplayNameOnly(item),
          price: formatMoney(item.price),
          priceCents: item.price,
        },
      });
    });
    if (!frames.length) return;
    this.closeSolarGrauSelector();
    if (window.matchMedia('(min-width: 750px)').matches) {
      document.dispatchEvent(
        new CustomEvent('zrzs:quick-add:close-cart-desktop')
      );
    }
    // openBatch reseta this.lensFlow.history pra começar o lote do zero —
    // isso sozinho já invalida qualquer "resumir de onde parou" pendente de
    // um card/PDP abandonado antes (ver canResume em handlePendingOpen), já
    // que o history dali deixa de corresponder ao pendingFrame guardado.
    // Sem limpar isPendingCartAdd/pendingFrame aqui, reabrir aquele produto
    // depois resolvia canResume=true e resumia pra dentro do history do
    // grau retroativo (produto/etapa errados) — resumir não se aplica a
    // este fluxo, então garante que ele nunca "herde" uma sessão alheia.
    this.isPendingCartAdd = false;
    this.pendingFrame = null;
    this.open();
    this.lensFlowExitPanel = 'cart';
    this.lensFlow.openBatch(frames, (/** @type {Array<any>} */ results) =>
      this.handleSolarGrauBatchComplete(results)
    );
  }

  /**
   * Aplica cada frame configurado: adiciona a unidade com grau como linha
   * nova primeiro (addFrameWithLensToCart, já enfileirado — mesma fila que
   * corrige a corrida de brinde duplicado) e só depois reduz 1 unidade da
   * linha original (updateItemGroup, já enfileirado). Kit: "move" o MESMO
   * kit já entregue pra linha nova (kitVariantId do sibling encontrado em
   * handleSolarGrauSelectorContinue) — nunca duplica (não é um kit novo,
   * é o mesmo variant_id), mas também nunca desaparece: sem repor aqui,
   * o decremento da armação antiga abaixo zerava o kit junto (mesmo
   * _grupo) sem nenhuma linha de reposição (bug real relatado pelo
   * usuário — o estojo sumia do pedido de verdade, confirmado até no
   * checkout).
   *
   * A ORDEM importa pra brinde: se a armação original era a única elegível
   * (_eligibleForGift_*) e ela for removida ANTES da nova existir,
   * updateItemGroup#syncGiftsOnRemove vê "nenhuma armação elegível
   * restante" e remove o brinde à toa (bug real relatado pelo usuário).
   * Adicionar primeiro — com o mesmo giftsData do produto, senão a nova
   * linha nunca reconquista a elegibilidade — e só então reduzir a antiga
   * faz o "nenhuma elegível restante" já encontrar a substituta no lugar.
   * @param {Array<any>} results
   * @returns {Promise<boolean>}
   */
  async handleSolarGrauBatchComplete(results) {
    try {
      for (const result of results) {
        await addFrameWithLensToCart({
          frameVariantId: result.frameVariantId,
          frameVariantTitle: result.frameVariantTitle || '',
          kitVariantId: result.kitVariantId || null,
          kitCaseBanner: result.kitCaseBanner || null,
          kitCaseDescription: result.kitCaseDescription || null,
          lens: result.lens,
          prescription: result.prescription,
          framePriceCents: result.productSummary?.priceCents || 0,
          giftsData: result.giftsData || null,
        });
        // Garante lastCartItems com a linha nova antes de decrementar a
        // antiga — o CartAddEvent da adição já atualiza o estado, mas um GET
        // explícito não depende da ordem dos listeners.
        await this.cartPanelController.refreshCartPanel();
        /** @param {string} key */
        const findByKey = key =>
          (this.cartPanelController.lastCartItems || []).find(
            /** @param {any} i */ i => i.key === key
          );
        let currentItem = findByKey(result.cartItemKey);
        if (!currentItem) {
          // Linha original não achada no 1º refresh — tenta mais uma vez
          // antes de desistir (bug relatado: armação ficava duplicada,
          // uma linha "antiga" sem reduzir ao lado da nova configurada;
          // sem retry nem log, isso passava batido, silencioso).
          await this.cartPanelController.refreshCartPanel();
          currentItem = findByKey(result.cartItemKey);
        }
        if (currentItem) {
          await this.cartPanelController.updateItemGroup(
            currentItem,
            currentItem.quantity - 1
          );
        } else {
          // eslint-disable-next-line no-console
          console.error(
            '[QuickAddOverlay] grau retroativo: não encontrei a linha original (cartItemKey=%s) pra decrementar depois de adicionar a versão com grau — a armação provavelmente ficou duplicada no carrinho.',
            result.cartItemKey
          );
        }
      }
      this.openCartPanel();
      return true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        '[QuickAddOverlay] Erro ao configurar grau retroativo:',
        error
      );
      return false;
    } finally {
      this.lensFlowExitPanel = 'confirm';
    }
  }
}

if (!customElements.get('zrzs-quick-add-overlay')) {
  customElements.define('zrzs-quick-add-overlay', ZRZSQuickAddOverlay);
}
